/**
 * Whether capture was entered by an in-app push, in THIS page session.
 *
 * ## The bug this exists to fix
 *
 * Capture's Back used `router.canGoBack()` to decide between popping and
 * replacing with a fallback, on the stated premise that after a reload "the
 * stack is rebuilt empty, so `back()` has nothing to pop and silently no-ops".
 *
 * That premise is false, and a Playwright trace is what settled it. The URL
 * sequence on a journey that reloads capture before pressing Back was:
 *
 * ```
 * /scouting?tab=fixtures → /quick-capture/<id> → /
 * ```
 *
 * `/` is not a destination either branch of that handler can replace to, so the
 * only way to reach it is `router.back()` — which means `canGoBack()` answered
 * TRUE. Expo Router rebuilds a reloaded deep link with the root route beneath
 * it, so there IS an entry to pop; it is simply the app index rather than the
 * screen the scout came from. Back did not no-op. It sent the scout Home, out
 * of the surface entirely, from a report they had just filed.
 *
 * ## Why a module flag rather than a router question
 *
 * `canGoBack()` cannot tell a real prior screen from a synthesized root, and
 * neither can the navigation state — a rebuilt stack and a genuine push both
 * read as two routes. So the question has to be answered by the thing that
 * actually differs: whether THIS page session performed the navigation.
 *
 * Module scope is exactly that scope. A reload re-evaluates the module and the
 * flag returns to `false`, which is the correct answer after a reload; an
 * in-app push sets it, which is the correct answer for a pop. Nothing has to be
 * cleaned up on unmount, because being wrong in the safe direction — replacing
 * with the known entry surface — is where this lands by default.
 *
' * Deliberately NOT a URL param. A `?from=` would survive the reload, which
 * sounds like a feature and is the defect: it would tell Back to pop a history
 * entry that the reload destroyed.
 *
 * The flag is an Apollo reactive var rather than a module-level binding because
 * this repo bans both mutable bindings (`functional/no-let`) and in-place
 * mutation (`functional/immutable-data`). A reactive var is the house pattern
 * for exactly this shape — `posthogIdentifiedVar` is the same boolean session
 * flag — and it carries the semantics for free: in memory, module scoped, gone
 * on reload.
 * @module features/match-report-capture/utils/captureEntry
 */
import { makeVar } from "@apollo/client";

/** Set only by an in-app navigation into capture, in this page session. */
const captureEnteredByPushVar = makeVar(false);

/**
 * Records that capture is being opened by an in-app navigation.
 *
 * Call immediately before pushing `/quick-capture/[matchId]`, from every site
 * that pushes it — a caller that forgets does not break Back, it only makes it
 * replace with the fallback instead of popping, which is the safe direction.
 */
export const markCaptureEntry = (): void => {
  captureEnteredByPushVar(true);
};

/**
 * Whether capture may safely pop back to the screen that pushed it.
 * @returns True when this page session pushed capture; false after a reload or
 * on a cold deep link, where there is no real screen beneath to return to.
 */
export const wasCaptureEnteredByPush = (): boolean => captureEnteredByPushVar();

/**
 * Forgets the entry, so a later cold visit cannot inherit this one's answer.
 *
 * Called by capture as it leaves. Without it a scout who enters by push, backs
 * out, and then deep-links straight into another capture would still be told
 * there is something to pop.
 */
export const clearCaptureEntry = (): void => {
  captureEnteredByPushVar(false);
};
